import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import axios from 'axios';
import moment from 'moment';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { ApiBaseUrlContext } from '../App';		
import * as XLSX from "xlsx";						  													  
import { useTranslation } from 'react-i18next';
import { ReactComponent as FilterIcon } from '../assets/svg/icon-filter.svg';
import { ReactComponent as AddIcon } from '../assets/svg/icon-add.svg';
import { ReactComponent as SectionToggleArrow } from '../assets/svg/icon-toggle-section-arrow.svg';
import { ReactComponent as DragIcon } from '../assets/svg/icon-drag-item.svg';
import { ReactComponent as RemoveItemIcon } from '../assets/svg/icon-remove-1.svg';
import './MaintenanceSchedule.css';
import '../boxitem.css';
import '../topmenu.css';
import '../pageSearch.css';
import '../forms.css';
import BoxItem from './BoxItem';
import { jwtDecode } from 'jwt-decode';
import { useLocation } from 'react-router-dom';
import { formatDateByLocale } from '../utils/dateUtils';
import EmailNotificationSettings from './EmailNotificationSettings'; // new component
import { useDropzone } from 'react-dropzone';
import GroupedDataTable from './GroupDataTable2'; 

const ChecklistItem = ({
  item,
  index,
  moveChecklistItem,
  handleChecklistItemChange,
  handleChecklistItemStatusChange,
  handleDeleteChecklistItem,
  handleChecklistItemFilesChange,
  showCheckboxes
}) => {
  const { t } = useTranslation(); 
  const [{ isDragging }, ref] = useDrag({
    type: 'CHECKLIST_ITEM',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'CHECKLIST_ITEM',
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveChecklistItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });
  const onDropImage = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]; // Only take the first file
      handleChecklistItemFilesChange(index, [
        Object.assign(file, { preview: URL.createObjectURL(file) })
      ]);
    }
  }, [index]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropImage,
    accept: 'image/*',
    multiple: false
  });
  const isHeader = item.status === 'header';
  const isText = item.status === 'text';
  const isImage = item.status=== 'file';
  return (


    <div
      ref={(node) => ref(drop(node))}
      className={`${isDragging ? 'dragging' : ''} ${isHeader ? 'header-item' : ''}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {isHeader ? (
		
        <div className="task-item task-item__header">
          <input
            type="text"
            className="task-item__input-text"
            value={item.description}
            onChange={(e) => handleChecklistItemChange(index, e.target.value)}
            placeholder="Header (click to edit)"
          />

          <span className="task-item__drag-handle" style={{ cursor: 'move' }}><DragIcon /></span>

          <button
            type="button"
            className="slim-button--remove"
            onClick={() => handleDeleteChecklistItem(index)}
          >
            <RemoveItemIcon />
          </button>
        </div>

      ) : isText ? (
        /* If it's a TEXT item */
        <div className="task-item task-item__text-block">
          {/* Show a read-only textarea or input */}
          <textarea
            className="task-item__input-text"
            value={item.description}
            readOnly
          />
  
          {/* You can still let them drag this item, or remove it */}
          <span className="task-item__drag-handle" style={{ cursor: 'move' }}>
            <DragIcon />
          </span>
  
          <button
            type="button"
            className="slim-button--remove"
            onClick={() => handleDeleteChecklistItem(index)}
          >
            <RemoveItemIcon />
          </button>
        </div>
          ) : isImage ?(
            <div
            ref={(node) => ref(drop(node))}
            className={`task-item task-item__image ${isDragging ? 'dragging' : ''}`}
            style={{ opacity: isDragging ? 0.5 : 1 }}
          >
            <div {...getRootProps()} className="image-upload-button" role="button" tabIndex={0}>
              <input {...getInputProps()} />
              <span>{isDragActive ? t('maintenanceSchedule.dropImagesHere') : t('maintenanceSchedule.clickOrDragImages')}</span>
            </div>
          
            <span className="task-item__drag-handle"><DragIcon /></span>
          
            <button
              type="button"
              className="slim-button--remove"
              onClick={() => handleDeleteChecklistItem(index)}
            >
              <RemoveItemIcon />
            </button>
          
            <div className="image-previews">
              {(item.files || []).map((file, i) => {
                const isImage = file.type?.startsWith('image/') || file.preview?.match(/\.(jpg|jpeg|png|gif)$/i);
                return (
                  <div key={i} className="image-preview">
                    {isImage ? (
                      <img src={file.preview} alt={file.name} />
                    ) : (
                      <a href={file.preview} target="_blank" rel="noopener noreferrer">
                        📄 {file.name || 'Open file'}
                      </a>
                    )}
                    <button onClick={() => {
                      const newFiles = item.files.filter((_, j) => j !== i);
                      handleChecklistItemFilesChange(index, newFiles);
                    }}>
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          
      ):(
		  
        <div className="task-item">
          <input
            type="text"
            className="task-item__input-text"
            value={item.description}
            onChange={(e) => handleChecklistItemChange(index, e.target.value)}
          />

          <span className="task-item__drag-handle" style={{ cursor: 'move' }}><DragIcon /></span>

          {showCheckboxes && (
            <input
              type="checkbox"
							
              checked={item.status === 'yes'}
              onChange={() => handleChecklistItemStatusChange(index, item.status === 'yes' ? 'no' : 'yes')}
            />
          )}
			
          <button
            type="button"
            className="slim-button--remove"
            onClick={() => handleDeleteChecklistItem(index)}
          >
            <RemoveItemIcon />
          </button>
        </div>

		   
      )}

    </div>
  );
};

const MaintenanceSchedule = () => {
  const location = useLocation();
  const apiBaseUrl = useContext(ApiBaseUrlContext);
  const { t, i18n } = useTranslation(); 
  const [machines, setMachines] = useState([]);
  const [groupNames, setGroupNames] = useState([]);
  const [closedList, setClosedList] = useState([]);
  const [maintenanceList, setMaintenanceList] = useState({
    open: [],
    overdue: [],
    today: [],
    tomorrow: [],
    upcoming: [],
  });
  const [upcomingStatusTime, setUpcomingStatusTime] = useState(86400); // 1 day in seconds as default
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [filter, setFilter] = useState({
    status: '',
    group: '',
    machine: '',
    startDate: '',
    endDate: '',
    searchText: ''
  });
  const [showFilter, setShowFilter] = useState(false);
  const [showContent, setShowContent] = useState(true);
  const [files, setFiles] = useState([]);
    // Store currency symbol and position in state
    const [currencySymbol, setCurrencySymbol] = useState('kr');
    const [currencyPosition, setCurrencyPosition] = useState('right');

  // 1) Ensure `material_cost` is in the default schedule object
  const [schedule, setSchedule] = useState({
    scheduleid: location.state || {},
    machineid: location.state || {},
    maintenance_type: location.state || {},
    next_maintenance_date: '',
    expected_duration: '',
    repeat_interval: '',
    repeat_enabled: true,
    upcoming_status_time: 86400,
    material_cost: 0,   
    note: '',
    descHeader: '',
    checklist: [],
    emailRecipient: '',       
    notificationDays: ''
  });
  const [newChecklistItem, setNewChecklistItem] = useState('');

  const [permissions, setPermissions] = useState({
    can_edit: false,
    can_delete: false,
    can_view: true
  });

      // A small helper to format a numeric value with the chosen currency symbol & position
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    const numeric = Number(value) || 0;
    const fixed = numeric.toFixed(2);
    return currencyPosition === 'left' ? `${currencySymbol}${fixed}` : `${fixed}${currencySymbol}`;
  };
  const addImageItem = () => {
    setSchedule(prev => ({
      ...prev,
      checklist: [
        ...prev.checklist,
        { status: 'file', files: [] }
      ]
    }));
  };
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);

  
      // Fetch user permissions based on role
      const fetchPermissions = async () => {
        try {
          const response = await axios.get(
            `${apiBaseUrl}/mnt/api/roles/permissions/${decodedToken.user_rights}`
          );
          setPermissions({
            can_edit: response.data.some(
              (permission) => permission.permission_name === 'can_edit' && permission.can_edit === 1
            ),
            can_view: response.data.some(
              (permission) => permission.permission_name === 'can_view' && permission.can_view === 1
            ),
            can_delete: response.data.some(
              (permission) => permission.permission_name === 'can_delete' && permission.can_delete === 1
            ),
          });
        } catch (error) {
          console.error('Error fetching permissions:', error);
        }
      };

      fetchPermissions();
    }
  }, [apiBaseUrl]);
  useEffect(() => {
    axios.post(`${apiBaseUrl}/mnt/api/maintenance/email/check-notifications`)
      .then((res) => {
        console.log('Notification emails processed', res.data);
      })
      .catch((err) => console.error('Error processing notifications:', err));
  }, [apiBaseUrl]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/mnt/api/settings`);
  
      console.log("Fetched settings response:", response.data);
  
      const settings = response.data;
  
      // Defensive: set default values first
      setCurrencySymbol(settings?.currencySymbol ?? 'kr');
      setCurrencyPosition(settings?.currencyPosition ?? 'right');
  
      // Defensive: check presence before accessing upcomingStatusTimeDays
      if (settings && typeof settings.upcomingStatusTimeDays !== 'undefined') {
        const days = Number(settings.upcomingStatusTimeDays);
        if (!isNaN(days)) {
          setUpcomingStatusTime(days * 86400);
        } else {
          console.warn('Invalid upcomingStatusTimeDays format, falling back to default');
          setUpcomingStatusTime(86400);
        }
      } else {
        console.warn('Missing upcomingStatusTimeDays in settings, using default of 1 day');
        setUpcomingStatusTime(86400);
      }
  
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, [apiBaseUrl]);
  
  
  const removeFile = (file) => {
    setFiles((curr) => curr.filter(f => f !== file));
  };
  const handleChecklistItemFilesChange = (idx, files) => {
    setSchedule(prev => {
      const ch = [...prev.checklist];
      ch[idx] = { ...ch[idx], files };
      return { ...prev, checklist: ch };
    });
  };
/*  
  const onDrop = useCallback(acceptedFiles => {
    setFiles(prev => [
      ...prev,
      ...acceptedFiles.map(file =>
        Object.assign(file, {
          preview: URL.createObjectURL(file)
        })
      )
    ]);
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/*,application/pdf',
    multiple: true
  });
  */

  const fetchMachinesAndGroups = useCallback(async () => {
    try {
      const [machinesResponse, groupsResponse] = await Promise.all([
        axios.get(`${apiBaseUrl}/mnt/api/machines/all`, { headers: { Accept: 'application/json' } }),
        axios.get(`${apiBaseUrl}/mnt/api/machines/get-group-names`, { headers: { Accept: 'application/json' } })
      ]);

      setMachines(machinesResponse.data);
      setGroupNames(groupsResponse.data);
    } catch (error) {
      console.error('Error fetching machines and groups:', error);
    }
  }, [apiBaseUrl]);
																					
  const categorizeMaintenance = (data) => {
    const currentDate = moment().startOf('day');
    const tomorrowDate = moment().add(1, 'days').startOf('day');

    const categorized = {
      open: [],
      overdue: [],
      today: [],
      tomorrow: [],
      upcoming: [],
    };

    data.forEach((item) => {
      const maintenanceDate = moment.unix(item.next_maintenance_date).startOf('day');

      if (item.status === 'Open') {
        categorized.open.push(item);
      } else if (maintenanceDate.isBefore(currentDate)) {
        categorized.overdue.push(item);
      } else if (maintenanceDate.isSame(currentDate, 'day')) {
        categorized.today.push(item);
      } else if (maintenanceDate.isSame(tomorrowDate, 'day')) {
        categorized.tomorrow.push(item);
      } else {
        categorized.upcoming.push(item);
      }
    });

    return categorized;
  };

  const fetchMaintenanceList = useCallback(async () => {
    const params = {
      status: filter.status, // can be 'Closed' or something else
      group: filter.group,
      machine: filter.machine,
      startDate: filter.startDate ? moment(filter.startDate).unix() : null,
      endDate: filter.endDate ? moment(filter.endDate).unix() : null,
      searchText: filter.searchText,
    };
  
    try {
      const response = await axios.get(`${apiBaseUrl}/mnt/api/maintenance`, {
        params,
        headers: { Accept: 'application/json' },
      });
      const data = response.data; // all results from the backend
  
      if (filter.status === 'Closed') {
        // Just store them in closedList
        setClosedList(data);
        // Clear out the normal list, so we don’t show any open/overdue/today, etc.
        setMaintenanceList({ open: [], overdue: [], today: [], tomorrow: [], upcoming: [] });
      } else {
        // Normal path: categorize them as before
        setClosedList([]); // Clear closedList so we don't see leftover closed items
        setMaintenanceList(categorizeMaintenance(data));
      }
    } catch (error) {
      console.error('Error fetching maintenance list:', error);
    }
  }, [apiBaseUrl, filter]);
  

  useEffect(() => {
    fetchSettings();
    fetchMachinesAndGroups();
    fetchMaintenanceList();
  }, [fetchSettings, fetchMachinesAndGroups, fetchMaintenanceList]);

   
  // 2) Make sure `material_cost` is included in the data sent to the server
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const filteredChecklist = schedule.checklist.filter(
        (item) =>
          (item.status === 'file') || (item.description && item.description.trim() !== '')
      );
      
      const nextMaintenanceDateEpoch = Math.floor(new Date(schedule.next_maintenance_date).getTime() / 1000);

      const repeatIntervalInSeconds =
        schedule.repeat_enabled && schedule.maintenance_type === 'custom'
          ? parseInt(schedule.repeat_interval, 10) * 86400
          : schedule.repeat_enabled
          ? parseInt(schedule.repeat_interval, 10)
          : null;
      
      const token = localStorage.getItem('token');
      const currentUserId = token ? jwtDecode(token).userid : null;
      
      const scheduleToSave = {
        ...schedule,
        user_id: currentUserId,
        next_maintenance_date: nextMaintenanceDateEpoch,
        repeat_interval: repeatIntervalInSeconds,
        checklist: filteredChecklist,
        // material_cost is already in schedule, so it will be included automatically
      };
      const formData = new FormData();
      formData.append('schedule', JSON.stringify(scheduleToSave));
      files.forEach(file => formData.append('files', file));
      schedule.checklist.forEach((item, idx) => {
        if (item.status === 'file') {
          item.files.forEach(file => {
            formData.append(`checklist_images[${idx}]`, file);
          });
        }
      });
      if (schedule.scheduleid) {
        await axios.patch(
          `${apiBaseUrl}/mnt/api/maintenance/${schedule.scheduleid}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        alert(t('maintenanceSchedule.updatedSuccessfully'));
      } else {
        await axios.post(
          `${apiBaseUrl}/mnt/api/maintenance`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        alert(t('maintenanceSchedule.createdSuccessfully'));
      }
      fetchMaintenanceList();
      setShowForm(false);
      setShowContent(true);
    } catch (error) {
      window.scrollTo(0,0);
      console.error('Error saving maintenance schedule:', error);
    }
  };

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // CHECKLIST ITEM FUNCTIONS
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      setSchedule({
        ...schedule,
        checklist: [...schedule.checklist, { description: newChecklistItem, status: 'no' }]
      });
      setNewChecklistItem('');
    }
  };

  const addHeaderItem = () => {
    if (newChecklistItem.trim()) {
      setSchedule({
        ...schedule,
        checklist: [...schedule.checklist, { description: newChecklistItem, status: 'header' }]
      });
      setNewChecklistItem('');
    }
  };
  const addTextItem = () => {
    setSchedule((prev) => ({
      ...prev,
      checklist: [
        ...prev.checklist,
        { description: `${t('maintenanceSchedule.descriptionAddTextHere')}`, status: 'text' },
      ],
    }));
  };
  

  const handleDeleteChecklistItem = (index) => {
    const updatedChecklist = [...schedule.checklist];
    updatedChecklist.splice(index, 1);
    setSchedule({ ...schedule, checklist: updatedChecklist });
  };

  const handleChecklistItemChange = (index, value) => {
    const updatedChecklist = [...schedule.checklist];
    updatedChecklist[index].description = value;
    setSchedule({ ...schedule, checklist: updatedChecklist });
  };

  const handleChecklistItemStatusChange = (index, status) => {
    const updatedChecklist = [...schedule.checklist];
    updatedChecklist[index].status = status;
    setSchedule({ ...schedule, checklist: updatedChecklist });
  };

  const moveChecklistItem = (fromIndex, toIndex) => {
    const updatedChecklist = [...schedule.checklist];
    const [movedItem] = updatedChecklist.splice(fromIndex, 1);
    updatedChecklist.splice(toIndex, 0, movedItem);
    setSchedule({ ...schedule, checklist: updatedChecklist });
  };

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // CRUD + DUPLICATE
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  const handleDelete = async (scheduleid) => {
    if (window.confirm(t('maintenanceSchedule.confirmDelete'))) {
      try {
        await axios.delete(`${apiBaseUrl}/mnt/api/maintenance/${scheduleid}`);
        fetchMaintenanceList();
      } catch (error) {
        console.error('Error deleting maintenance schedule:', error);
      }
    }
  };

  const handleDuplicate = async (item) => {
    try {
      const checklistResponse = await axios.get(
        `${apiBaseUrl}/mnt/api/maintenance/${item.scheduleid}/checklist`
      );
  
      const checklistItems = checklistResponse.data.map((checklistItem) => {
        const { status, description } = checklistItem;
        const base = { description, status };
  
        if (status === 'file') {
          const normalizedPath = description.replaceAll('\\', '/');
          const previewUrl = `${apiBaseUrl}/mnt/api/uploads/${normalizedPath}`;
          return {
            ...base,
            files: [{
              name: normalizedPath.split('/').pop(),
              preview: previewUrl,
              type: normalizedPath.endsWith('.pdf') ? 'application/pdf' : 'image/*'
            }]
          };
        }
  
        return base;
      });
  
      setSchedule({
        scheduleid: '',
        machineid: item.machineid,
        maintenance_type: item.maintenance_type,
        next_maintenance_date: '',
        expected_duration: item.expected_duration,
        repeat_interval: item.repeat_interval,
        repeat_enabled: item.repeat_enabled,
        upcoming_status_time: upcomingStatusTime,
        descHeader: item.descHeader || '',
        note: item.note,
        material_cost: item.material_cost || '',
        emailRecipient: item.emailRecipient || '',
        notificationDays: item.notificationDays || '',
        checklist: checklistItems
      });
  
      setIsEditing(false);
      setShowForm(true);
      setShowContent(false);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error duplicating checklist items:', error);
    }
  };
  
  const handleEdit = async (item) => {
    try {
      const checklistResponse = await axios.get(
        `${apiBaseUrl}/mnt/api/maintenance/${item.scheduleid}/checklist`
      );
      const checklistItems = checklistResponse.data.map((item) => {
        const base = {
          description: item.description,
          status: item.status
        };
  
        if (item.status === 'file') {
          const previewUrl = `${apiBaseUrl}/mnt/api/uploads/${item.description}`;
          return {
            ...base,
            files: [{
              name: item.description.split('/').pop(),
              preview: previewUrl
            }]
          };
        }
  
        return base;
      });
  
      setSchedule({
        scheduleid: item.scheduleid,
        machineid: item.machineid,
        maintenance_type: item.maintenance_type,
        // ✅ Format to yyyy-MM-dd for input type="date"
        next_maintenance_date: moment.unix(item.next_maintenance_date).format('YYYY-MM-DD'),
        expected_duration: item.expected_duration,
        repeat_interval: item.maintenance_type === 'custom'
          ? item.repeat_interval / 86400
          : item.repeat_interval,
        repeat_enabled: item.repeat_enabled,
        upcoming_status_time: upcomingStatusTime,
        descHeader: item.descHeader || '',
        note: item.note,
        material_cost: item.material_cost || '',
        emailRecipient: item.emailRecipient || '',             // ✅ Include emailRecipient
        notificationDays: item.notificationDays || '',         // ✅ Include notificationDays
        checklist: checklistItems
      });
  
      setIsEditing(true);
      setShowForm(true);
      setShowContent(false);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error fetching checklist items:', error);
    }
  };
  


  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // FILTERS
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  const handleFilterChange = (e) => {
    setFilter({ ...filter, [e.target.name]: e.target.value });
  };

  const applyFilters = async () => {
    try {
      await fetchMaintenanceList();
    } catch (error) {
      console.error('Error applying filters:', error);
    }
  };

  const clearFilters = () => {
    setFilter({
      status: '',
      group: '',
      machine: '',
      startDate: '',
      endDate: '',
      searchText: ''
    });
    fetchMaintenanceList();
  };

 const handleOpen = async (item) => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found, redirecting to login');
      window.location.href = '/login';
      return;
    }

    if (item.status === 'Open') {
      console.log('The maintenance is already open.');
      window.location.href = `/mnt/operator/${item.scheduleid}`;
      return;
    }

    try {
      await axios.patch(
        `${apiBaseUrl}/mnt/api/maintenance/${item.scheduleid}/status`,
        { status: 'Open' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = `/mnt/operator/${item.scheduleid}`;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.error('Unauthorized, redirecting to login');
        window.location.href = '/login';
      } else {
        console.error('Error updating maintenance status:', error);
      }
    }
  };

  
  

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // CSV and PDF
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  const escapeCSVField = (field) => {
    if (typeof field === 'string') {
      return `"${field.replace(/"/g, '""')}"`; // Escape double quotes
    }
    return field; // Return non-string values as-is
  };

  const formatChecklistItems = (checklistDescriptions) => {
    return checklistDescriptions
      .split('|')
      .map((item) => item.trim())
      .join('\n');
  };

  // 5) Add the `material_cost` column/field to CSV
  const downloadCSV = () => {
    const csvContent = [
      [
        "Machine",
        "Group",
        "Maintenance Type",
        "Repeat Interval",
        "Description",
        "Scheduled Date",
        "Start Time",
        "End Time",
        "Duration",
        "Hourly Rate",
        "Expected Cost",
        "Material Cost"
      ],
    ];
  
    Object.keys(maintenanceList).forEach((category) => {
      maintenanceList[category].forEach((item) => {
        csvContent.push(getFormattedRow(item, escapeCSVField));
      });
    });
  
    closedList.forEach((item) => {
      csvContent.push(getFormattedRow(item, escapeCSVField));
    });
  
    // Convert to CSV format
    const csvString = csvContent.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "maintenance_schedule.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  
  
  //Excel function

  const downloadXLSX = () => {
    const wsData = [
      [
        "Machine",
        "Group",
        "Maintenance Type",
        "Repeat Interval",
        "Description",
        "Scheduled Date",
        "Start Time",
        "End Time",
        "Duration",
        "Hourly Rate",
        "Expected Cost",
        "Material Cost"
      ]
    ];
  
    Object.keys(maintenanceList).forEach((category) => {
      maintenanceList[category].forEach((item) => {
        wsData.push(getFormattedRow(item));
      });
    });
  
    closedList.forEach((item) => {
      wsData.push(getFormattedRow(item));
    });
  
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Maintenance Schedule");
  
    XLSX.writeFile(wb, "maintenance_schedule.xlsx");
  };

  const getFormattedRow = (item, formatFn = (x) => x) => {
    const machineName = machines.find((m) => m.machineid === item.machineid)?.machine || "Unknown Machine";
    const groupName = item.dncmaxgroupname || "Unknown Group";
    const maintenanceType = item.maintenance_type || "N/A";
    const repeatIntervalDays = item.repeat_interval ? item.repeat_interval / 86400 : "N/A";
    const description = item.note ? item.note.slice(0, 100) : "";
    const scheduledDate = new Date(item.next_maintenance_date * 1000).toISOString().split("T")[0];
    const startTime = item.open ? new Date(item.open * 1000).toISOString().replace("T", " ").split(".")[0] : "N/A";
    const endTime = item.closed ? new Date(item.closed * 1000).toISOString().replace("T", " ").split(".")[0] : "N/A";
    const hourly_rate = item.hourly_rate || "N/A";
  
    let duration = "N/A";
    if (item.open && item.closed) {
      const durationInSeconds = item.closed - item.open;
      const hours = Math.floor(durationInSeconds / 3600);
      const minutes = Math.floor((durationInSeconds % 3600) / 60);
      const seconds = durationInSeconds % 60;
      duration = `${hours}h ${minutes}m ${seconds}s`;
    }
  
    const rawExpectedCost = item.hourly_rate ? item.hourly_rate * item.expected_duration : 0;
    const expectedCostDisplay = formatCurrency(rawExpectedCost);
    const materialCostDisplay = formatCurrency(item.material_cost);
  
    return [
      formatFn(machineName),
      formatFn(groupName),
      formatFn(maintenanceType),
      formatFn(repeatIntervalDays),
      formatFn(description),
      formatFn(scheduledDate),
      formatFn(startTime),
      formatFn(endTime),
      formatFn(duration),
      formatFn(formatCurrency(hourly_rate)),
      formatFn(expectedCostDisplay),
      formatFn(materialCostDisplay || "N/A")
    ];
  };
  

  // 6) Add `material_cost` to the PDF

  const generatePDF = async (item) => {
    const doc = new jsPDF();
    const logoUrl = `${apiBaseUrl}/mnt/api/logo`;
  
    // Load company logo
    try {
      const response = await fetch(logoUrl);
      if (response.ok) {
        const blob = await response.blob();
        const reader = new FileReader();
        const logoData = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        doc.addImage(logoData, 'PNG', 20, 13, 42, 14);
      }
    } catch (error) {
      console.error('Error loading logo:', error);
    }
  
    const machineName = machines.find((m) => m.machineid === item.machineid)?.machine || t('maintenanceSchedule.unknownMachine');
    doc.setFontSize(18);
    doc.text(t('maintenanceSchedule.maintenanceSchedulePDF'), 105, 20, null, null, 'center');
  
    doc.setFontSize(12);
    doc.text(`${t('maintenanceSchedule.machine')}: ${machineName}`, 20, 40);
    doc.text(`${t('maintenanceSchedule.maintenanceType')}: ${item.maintenance_type}`, 20, 50);
    doc.text(`${t('maintenanceSchedule.scheduledDate')}: ${formatDateByLocale(Math.floor(item.next_maintenance_date), i18n.language)}`, 20, 60);
    doc.text(`${t('maintenanceSchedule.status')}: ${item.status}`, 20, 70);
  
    const expectedCost = item.hourly_rate ? item.hourly_rate * item.expected_duration : 0;
    doc.text(`${t('maintenanceSchedule.expectedCost')}: ${formatCurrency(expectedCost)}`, 20, 80);
    doc.text(`${t('maintenanceSchedule.materialCost')}: ${formatCurrency(item.material_cost)}`, 20, 90);
  
    const wrappedNote = doc.splitTextToSize(`${t('maintenanceSchedule.description')}: ${item.note || ''}`, 180);
    const descriptionY = 100;
    doc.text(wrappedNote, 20, descriptionY);
  
    let currentY = descriptionY + wrappedNote.length * 6 + 10;
    doc.setFontSize(14);
    doc.text(`${t('maintenanceSchedule.checklistTask')}:`, 20, currentY);
    currentY += 8;
  
    const rows = [];
    const links = [];
    const checklistItems = (item.checklist_descriptions || '').split('|').filter(Boolean);
  
    for (const desc of checklistItems) {
      const isFile = desc.startsWith('maintenance\\') || desc.startsWith('maintenance/');
      const fileName = desc.split('\\').pop().split('/').pop();
      const isImage = /\.(jpg|jpeg|png|gif)$/i.test(desc);
  
      if (isFile && isImage) {
        rows.push([
          { content: 'See image below', styles: { fontStyle: 'italic', textColor: [100, 100, 100] } },
          ''
        ]);
      } else if (isFile) {
        const url = `${apiBaseUrl}/mnt/api/uploads/${desc.replaceAll('\\', '/')}`;
        rows.push([
          { content: fileName, styles: { textColor: [0, 0, 255], fontStyle: 'italic' } },
          ''
        ]);
        rows.push([
          { content: t('maintenanceSchedule.reminderToPrintFiles'), colSpan: 2, styles: { fontStyle: 'italic', textColor: [100, 100, 100] } },
          ''
        ]);
        links.push({ text: fileName, url }); // 👍 Uses the correct URL now
      } else {
        rows.push([
          { content: desc, styles: { fontStyle: 'normal' } },
          ''
        ]);
      }
    }
  
    // Render checklist table
    doc.autoTable({
      startY: currentY,
      head: [
        [
          t('maintenanceSchedule.checklistTask'),
          t('maintenanceSchedule.checklistCompleted')
        ]
      ],
      body: rows,
      theme: 'grid',
      styles: { fontSize: 10 },
      headStyles: { fillColor: [160, 160, 160] }
    });
  
   
    let finalY = doc.lastAutoTable.finalY + 10;
  
    for (const desc of checklistItems) {
      const isFile = desc.startsWith('maintenance\\') || desc.startsWith('maintenance/');
      const isImage = /\.(jpg|jpeg|png|gif)$/i.test(desc);
  
      if (isFile && isImage) {
        try {
          const imageUrl = `${apiBaseUrl}/mnt/api/uploads/${desc.replaceAll('\\', '/')}`;
          const imgResp = await fetch(imageUrl);
          const imgBlob = await imgResp.blob();
          const reader = new FileReader();
  
          const base64 = await new Promise((resolve) => {
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(imgBlob);
          });
  
          // Ensure space before inserting image
          if (finalY + 50 > 280) {
            doc.addPage();
            finalY = 20;
          }
  
          doc.addImage(base64, 'JPEG', 20, finalY, 60, 45);
          finalY += 50;
  
        } catch (err) {
          console.warn(`Could not load image: ${desc}`, err);
        }
      }
    }
  
    doc.save(`${t('maintenanceSchedule.maintenanceSchedulePDF')}_${item.scheduleid}.pdf`);
  };
  
const addColor = (items, color) =>
  items.map((i) => ({ ...i, _color: color }));


  
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // RENDER
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  return (

    <>

      <DndProvider backend={HTML5Backend}>

        {showFilter && (
          <div
            className="filter-container"
            onClick={() => setShowFilter(!showFilter)}
          >
            <div className="filter" onClick={(e) => e.stopPropagation()}>
              <div className="filter__fields">
                <label className="filter__label">{t('maintenanceSchedule.status')}</label>
                <div className="entry-item">
                  <select
                    className={`form-select-drop`}
                    name="status"
                    value={filter.status}
                    onChange={handleFilterChange}
                  >
                    <option value="">
                      {t('maintenanceSchedule.allExcludingClosed')}
                    </option>
                    <option value="New">{t('maintenanceSchedule.new')}</option>
                    <option value="Upcoming">
                      {t('maintenanceSchedule.upcoming')}
                    </option>
                    <option value="Open">{t('maintenanceSchedule.open')}</option>
                    <option value="Overdue">
                      {t('maintenanceSchedule.overdue')}
                    </option>
                    <option value="Closed">{t('maintenanceSchedule.closed')}</option>
                  </select>
                </div>

                <label className="filter__label">{t('maintenanceSchedule.groupName')}</label>
                <div className="entry-item">
                  <select 
                    id="group" 
                    name="group" 
                    value={filter.group} 
                    onChange={handleFilterChange}
                    className={`form-select-drop`}
                    >
                    <option value="">{t('maintenanceSchedule.selectGroup')}</option>
                    {groupNames.map((group) => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                  </select>
                </div>

                <label className="filter__label">{t('maintenanceSchedule.machine')}</label>
                <div className="entry-item">
                  <select
                    className={`form-select-drop`}
                    name="machine"
                    value={filter.machine}
                    onChange={handleFilterChange}
                  >
                    <option value="">{t('maintenanceSchedule.all')}</option>
                    {machines.map((machine) => (
                      <option key={machine.machineid} value={machine.machineid}>
                        {machine.machine}
                      </option>
                    ))}
                  </select>
                </div>

                <label className="filter__label">{t('maintenanceSchedule.startDate')}</label>
                <div className="entry-item">
                  <input
                    type="date"
                    className="form-control"
                    name="startDate"
                    value={filter.startDate}
                    onChange={handleFilterChange}
                  />
                </div>

                <label className="filter__label">{t('maintenanceSchedule.endDate')}</label>
                <div className="entry-item">
                  <input
                    type="date"
                    className="form-control"
                    name="endDate"
                    value={filter.endDate}
                    onChange={handleFilterChange}
                  />
                </div>

                <label className="filter__label">{t('maintenanceSchedule.search')}</label>
                <div className="entry-item">
                  <input
                    type="text"
                    className="form-control"
                    name="searchText"
                    placeholder={t('maintenanceSchedule.searchPlaceholder')}
                    value={filter.searchText}
                    onChange={handleFilterChange}
                  />
                </div>
              </div>

              <div className="filter__buttons">
                <button type="button" className="button--grey" onClick={clearFilters}>
                  {t('maintenanceSchedule.clearFilters')}
                </button>
              </div>

            </div>
          </div>

        )}

        {showForm && (

          <form onSubmit={handleSubmit}>

            <div className="modal-full">
		
              <div className="top-menu">
                <button type="submit" className="button--blue">
                {t('maintenanceSchedule.ButtonSave')}
                </button>
                <div className="top-menu__button-space"></div>
                <button
                  type="button"
                  className="button--grey"
                  onClick={() => {
                    setShowForm(false);
                    setShowContent(true);
                    window.scrollTo(0, 0);
                  }}
                >
                  {t('maintenanceSchedule.ButtonCancel')}
                </button>
              </div>

              <div className="content__main-title">
                {t('maintenanceSchedule.createOrUpdate')}
											   
				   
              </div>
										

              <div className="form-section__title">{t('maintenanceSchedule.TitleMachine')}</div>
              <div className="form-section">
                <div className="entry-item entry-item__content-width">
                  <label htmlFor="machine" className="entry-label">
                    {t('maintenanceSchedule.machine')}
                  </label>
                  <select
                    id="machine"
                    className="entry-control form-select"
                    value={schedule.machineid}
                    onChange={(e) => setSchedule({ ...schedule, machineid: e.target.value })}
                    required
                  >
                    <option value="">
                      {t('maintenanceSchedule.selectMachine')}
                    </option>
                    {machines.map((machine) => (
                      <option key={machine.machineid} value={machine.machineid}>
                        {machine.machine}
                      </option>
                    ))}
                  </select>
                </div>

              </div>

              
					
				      <div className="form-section__title">Email Notification</div>
                {/* Insert our email notification settings here */}
                <EmailNotificationSettings
                  apiBaseUrl={apiBaseUrl}
                  schedule={schedule}
                  setSchedule={setSchedule}
                />

              <div className="form-section__title">{t('maintenanceSchedule.TitleInterval')}</div>       
              <div className="form-section">

                <div className="entry-item entry-item__content-width">
                  <label htmlFor="type" className="entry-label">
                    {t('maintenanceSchedule.maintenanceType')}
                  </label>
                  <select
                    id="type"
                    className="entry-control form-select"
                    value={schedule.maintenance_type}
                    
                    onChange={(e) => {
                      const value = e.target.value;
                      let repeatInterval = '';

                      if (value !== 'custom') {
                        const intervals = {
                          select: null,
                          daily: 86400,
                          weekly: 604800,
                          monthly: 2592000,
                          quarterly: 7776000,
                          half_year: 15552000,
                          annually: 31104000,
                        };
                        repeatInterval = intervals[value];
                      }

                      setSchedule({
                        ...schedule,
                        maintenance_type: value,
                        repeat_interval: repeatInterval,
                      });
                    }}
                    required
                  >
                    <option value="">
                      {t('maintenanceSchedule.selectInterval')}
                    </option>
                    <option value="daily">{t('maintenanceSchedule.daily')}</option>
                    <option value="weekly">{t('maintenanceSchedule.weekly')}</option>
                    <option value="monthly">{t('maintenanceSchedule.monthly')}</option>
                    <option value="quarterly">{t('maintenanceSchedule.quarterly')}</option>
                    <option value="half_year">{t('maintenanceSchedule.halfYear')}</option>
                    <option value="annually">{t('maintenanceSchedule.annually')}</option>
                    <option value="onetime">{t('maintenanceSchedule.oneTime')}</option>
                    <option value="custom">{t('maintenanceSchedule.custom')}</option>
                  </select>
                </div>

                {schedule.maintenance_type === 'custom' && (
                  <div className="entry-item entry-item__small">
                    <label htmlFor="type-custom" className="entry-label">
                      {t('maintenanceSchedule.customRepeatInterval')}
                    </label>
                    <input
                      id="type-custom"
                      type="number"
                      className="maintenance-schedule-form-input"
                      value={schedule.repeat_interval || ''}
                      onChange={(e) =>
                        setSchedule({ ...schedule, repeat_interval: e.target.value })
                      }
                      required={schedule.maintenance_type === 'custom'}
                      min="1"
                    />
                  </div>
                )}

                <div className="entry-item entry-item__small">
                  <label htmlFor="date" className="entry-label">
                    {t('maintenanceSchedule.nextMaintenanceDate')}:
                  </label>
                  <input
                    id="date"
                    type="date"
							
                    value={schedule.next_maintenance_date}
                    onChange={(e) =>
                      setSchedule({ ...schedule, next_maintenance_date: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="entry-item entry-item__small">
                  <label htmlFor="duration" className="entry-label">
                    {t('maintenanceSchedule.expectedDuration')}:
                  </label>
                  <input
                    id="duration"
                    type="number"
							
                    value={schedule.expected_duration}
                    onChange={(e) =>
                      setSchedule({ ...schedule, expected_duration: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="entry-item entry-item__small">
                  <label htmlFor="enabled" className="entry-label">
                    {t('maintenanceSchedule.repeatAtSameIntervals')}:
                  </label>
                  <input
                    id="enabled"
                    type="checkbox"
							
                    checked={schedule.repeat_enabled}
                    onChange={(e) =>
                      setSchedule({ ...schedule, repeat_enabled: e.target.checked })
                    }
                  />
                </div>
              </div>

              {/* 
                 7) Add the material_cost field here in the form 
              */}
              <div className="form-section__title">
              {t('maintenanceSchedule.TitleCosts')}
              </div>
              <div className="form-section">
                <div className="entry-item entry-item__small">
                  <label htmlFor="materialCost" className="entry-label">
                    {t('maintenanceSchedule.materialCost')}
                  </label>
                  <input
                    id="materialCost"
                    type="number"
                    value={schedule.material_cost}
                    onChange={(e) =>
                      setSchedule({ ...schedule, material_cost: e.target.value === '' ? 0 : Number(e.target.value)})
                    }
                    required 
                  />
                </div>
              </div>


              <div className="form-section__title">
              {t('maintenanceSchedule.TitleMaintenanceChecklist')}
              </div>
              <div className="form-section">

                <div className="entry-item entry-item__full-width">
                  <label htmlFor="descHeader" className="entry-label">
                    {t('maintenanceSchedule.descriptionHeader')}
                  </label>
                  <input
                    id="descHeader"
                    type="text"
							
                    value={schedule.descHeader}
                    onChange={(e) =>
                      setSchedule({ ...schedule, descHeader: e.target.value })
                    }
                    placeholder=""
                  />
                </div>

                <div className="entry-item entry-item__full-width">
                  <label htmlFor="note" className="entry-label">
                    {t('maintenanceSchedule.noteInstructions')}:
                  </label>
                  <textarea
                    id="note"
							
                    value={schedule.note}
                    onChange={(e) =>
                      setSchedule({ ...schedule, note: e.target.value })
                    }
                  ></textarea>
                </div>

                <div className="form-section__inner-title">
                  {t('maintenanceSchedule.createChecklist')}
                </div>

                <div className="task-list">
                  {schedule.checklist.map((item, index) => (
                    <ChecklistItem
                      key={index}
                      item={item}
                      index={index}
                      moveChecklistItem={moveChecklistItem}
                      handleChecklistItemChange={handleChecklistItemChange}
                      handleChecklistItemStatusChange={handleChecklistItemStatusChange}
                      handleDeleteChecklistItem={handleDeleteChecklistItem}
                      handleChecklistItemFilesChange={handleChecklistItemFilesChange}
                      showCheckboxes={isEditing}
                    />
                  ))}
                </div>

                <div className="task-item__new">
                  <input
                    type="text"
                    className="entry-control"
                    value={newChecklistItem}
                    onChange={(e) => setNewChecklistItem(e.target.value)}
                    placeholder={t('maintenanceSchedule.checklistOrHeader')}
                  />
                </div>

                <div className="checklist-options">
                  <button
                    type="button"
                    className="button--icon-add button--grey"
                    onClick={addHeaderItem}
                  >
                    <AddIcon /> {t('maintenanceSchedule.addHeader')}
                  </button>
                  <button
                    type="button"
                    className="button--icon-add button--grey"
                    onClick={addChecklistItem}
                  >
                    <AddIcon /> {t('maintenanceSchedule.addChecklistItem')}
                  </button>
                  <button
  type="button"
  className="button--icon-add button--grey"
  onClick={addTextItem}
>
  <AddIcon /> {t('maintenanceSchedule.ButtonAddText')}
</button>
<button
  type="button"
  className="button--icon-add button--grey"
  onClick={addImageItem}
>
  <AddIcon /> {t('maintenanceSchedule.addImage')}
</button>
                </div>

              </div>

              <div className="form-submit">
                <button type="submit" className="button--blue">
                  {schedule.scheduleid
                    ? t('maintenanceSchedule.updateMaintenanceSchedule')
                    : t('maintenanceSchedule.createMaintenanceSchedule')}
                </button>
                <div className="top-menu__button-space"></div>
                <button
                  type="button"
                  className="button--grey"
                  onClick={() => {
                    setShowForm(false);
                    setShowContent(true);
                    window.scrollTo(0, 0);
                  }}
                >
                  {t('maintenanceSchedule.ButtonCancel')}
                </button>
              </div>

            </div>
          </form>

        )}

        {showContent && (

          <div>
		  
            <div className="content">

              <div className="top-menu">
                <div className="top-menu__divider"></div>
                <div
                  className="top-menu__icon-button top-menu__filter-button"
                  onClick={() => setShowFilter(!showFilter)}
                >
                  <FilterIcon />
                </div>

                {permissions.can_edit && (
                  <>
                    <button
                      type="button"
                      className="button--icon-add button--green"
                      onClick={() => {
                        setShowForm(true);
                        setShowContent(false);
                        setIsEditing(false);
                        setSchedule({
                          scheduleid: '',
                          machineid: '',
                          maintenance_type: '',
                          next_maintenance_date: '',
                          expected_duration: '',
                          repeat_interval: '',
                          repeat_enabled: true,
                          upcoming_status_time: upcomingStatusTime,
                          material_cost: '',  // <--- Ensure this is cleared
                          note: '',
                          checklist: []
                        });
                      }}
                    >
                      <AddIcon /> {t('maintenanceSchedule.addNew')}
                    </button>
                    <div className="top-menu__button-space"></div>
                  </>
                )}
                
                <div className="button-container">
                  <button onClick={downloadCSV}>
                    {t("maintenanceSchedule.downloadCSV")}
                  </button>

                  <div className="button-space"></div>  {/* ✅ This adds space between the buttons */}

                  <button onClick={downloadXLSX}>
                    {t("maintenanceSchedule.downloadXLSX")}
                  </button>
                </div>
              </div>

              <div className="content__main-title">
                {t('maintenanceSchedule.scheduledMaintenance')}
              </div>






<GroupedDataTable
  groupedData={{
    [t('maintenanceSchedule.open')]: addColor(
      maintenanceList.open.filter(item => machines.some(m => m.machineid === item.machineid)),
      "blue"
    ),
    [t('maintenanceSchedule.overdue')]: addColor(
      maintenanceList.overdue.filter(item => machines.some(m => m.machineid === item.machineid)),
      "red"
    ),
    [t('maintenanceSchedule.todaysTasks')]: addColor(
      maintenanceList.today.filter(item => machines.some(m => m.machineid === item.machineid)),
      "grey"
    ),
    [t('maintenanceSchedule.tomorrowsTasks')]: addColor(
      maintenanceList.tomorrow.filter(item => machines.some(m => m.machineid === item.machineid)),
      "grey"
    ),
    [t('maintenanceSchedule.upcoming')]: addColor(
      maintenanceList.upcoming.filter(item => machines.some(m => m.machineid === item.machineid)),
      "grey"
    ),
    [t('maintenanceSchedule.closed')]: addColor(
      closedList.filter(item => machines.some(m => m.machineid === item.machineid)),
      "grey"
    ),
  }}
  isFiltered={Object.values(filter).some(Boolean)}
  headers={[
    {
      label: t("maintenanceSchedule.machine"),
      render: (item) =>
        machines.find((m) => m.machineid === item.machineid)?.machine || t('maintenanceSchedule.unknownMachine'),
      accessor: "machineid",
      elasticCell: true,
      colorStyle: "block",
      colorValueFrom: (item) => item._color,
    },
    {
      label: t("maintenanceSchedule.descriptionHeader"),
      accessor: "descHeader",
      elasticCell: true,
    },
    {
      label: t("maintenanceSchedule.scheduled"),
      render: (item) =>
        formatDateByLocale(Math.floor(item.next_maintenance_date), i18n.language, false),
      hideMobile: true,
    },
    {
      label: t("maintenanceSchedule.repeat"),
      render: (item) =>
        item.repeat_enabled && item.repeat_interval
          ? item.maintenance_type === 'custom'
            ? t('maintenanceSchedule.repeatsEvery', { days: item.repeat_interval / 86400 })
            : t(`maintenanceSchedule.${item.maintenance_type}`)
          : 'N/A',
      hideMobile: true,
    },
    {
      label: t("maintenanceSchedule.Group"),
      render: (item) =>
        machines.find((m) => m.machineid === item.machineid)?.dncmaxgroupname || t('maintenanceSchedule.unknownGroup'),
      hideMobile: true,
    },
    {
      label: t("maintenanceSchedule.userid"),
      accessor: "operator",
      hideMobile: true,
    },
  ]}
  rowKeyFn={(item) => item.scheduleid}
  getPrimaryActionLabel={(item) =>
    item.status !== 'Open' ? t('maintenanceSchedule.start') : t('maintenanceSchedule.continue')
  }
  onPrimaryAction={(item) => handleOpen(item)}
  contextualOptionsFn={(item) =>
    item.status !== 'Open'
      ? [
          {
            label: t('maintenanceSchedule.edit'),
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              handleEdit(item);
            },
          },
          {
            label: t('maintenanceSchedule.downloadPDF'),
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              generatePDF(item);
            },
          },
          {
            label: t('maintenanceSchedule.duplicate'),
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDuplicate(item);
            },
          },
          {
            label: t('maintenanceSchedule.delete'),
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDelete(item.scheduleid);
            },
          },
        ]
      : [
          {
            label: t('maintenanceSchedule.downloadPDF'),
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              generatePDF(item);
            },
          },
        ]
  }
  groupMeta={{
    [t('maintenanceSchedule.open')]: {
      title: t('maintenanceSchedule.open'),
      description: t('maintenanceSchedule.openDesc'),
      emptyMessage: t('maintenanceSchedule.noOpenTasks'),
    },
    [t('maintenanceSchedule.overdue')]: {
      title: t('maintenanceSchedule.overdue'),
      description: t('maintenanceSchedule.overdueDesc'),
      emptyMessage: t('maintenanceSchedule.noOverdueTasks'),
    },
    [t('maintenanceSchedule.todaysTasks')]: {
      title: t('maintenanceSchedule.todaysTasks'),
      description: t('maintenanceSchedule.todayDesc'),
      emptyMessage: t('maintenanceSchedule.noTodayTasks'),
    },
    [t('maintenanceSchedule.tomorrowsTasks')]: {
      title: t('maintenanceSchedule.tomorrowsTasks'),
      description: t('maintenanceSchedule.tomorrowDesc'),
      emptyMessage: t('maintenanceSchedule.noTomorrowTasks'),
    },
    [t('maintenanceSchedule.upcoming')]: {
      title: t('maintenanceSchedule.upcoming'),
      description: t('maintenanceSchedule.upcomingDesc'),
      emptyMessage: t('maintenanceSchedule.noUpcomingTasks'),
    },
    [t('maintenanceSchedule.closed')]: {
      title: t('maintenanceSchedule.closed'),
      description: t('maintenanceSchedule.closedDesc'),
      emptyMessage: t('maintenanceSchedule.noClosedTasks'),
    },
  }}
/>





{/*


              {maintenanceList.open.length > 0 && (

                <>

                  <div className="content__section-header">
                    <div className="content__section-title">
                      {t('maintenanceSchedule.open')}
                      <div className="content__section-title-description">
                        {t('maintenanceSchedule.openDesc')}
                      </div>
                    </div>
                  </div>

                  <div className="content__machine-list">

                    {maintenanceList.open
                      .filter((item) => machines.find((m) => m.machineid === item.machineid))
                      .map((item) => {
                        const machine = machines.find((m) => m.machineid === item.machineid);
                        return (
                      <div key={item.scheduleid}>
                        <BoxItem
                          color="blue"
                          title={`${
                            machines.find((m) => m.machineid === item.machineid)?.machine ||
                            t('maintenanceSchedule.unknownMachine')
                          }`}
                          infoVisible={{
                            [t('maintenanceSchedule.scheduled')]: formatDateByLocale(
                              Math.floor(item.next_maintenance_date),
                              i18n.language,
                              false
                            ),
                            [t('maintenanceSchedule.repeat')]:
                              item.repeat_enabled && item.repeat_interval
                                ? item.maintenance_type === 'custom'
                                  ? t('maintenanceSchedule.repeatsEvery', {
                                      days: item.repeat_interval / 86400,
                                    })
                                  : t(`maintenanceSchedule.${item.maintenance_type}`)
                                : 'N/A',
                                [t('maintenanceSchedule.Group')]: machines.find((m) => m.machineid === item.machineid)?.dncmaxgroupname || t('maintenanceSchedule.unknownGroup'),
                                [t('maintenanceSchedule.userid')]: item.operator || 'None',
                          }}
                          desc={item.descHeader || null}
                          buttonAction={
                            item.status !== 'Open'
                              ? {
                                  name: t('maintenanceSchedule.start'),
                                  handler: () => handleOpen(item),
                                }
                              : {
                                  name: t('maintenanceSchedule.continue'),
                                  handler: () => handleOpen(item),
                                }
                          }
                          options={
                            item.status !== 'Open'
                              ? {
                                  [t('maintenanceSchedule.edit')]: () => handleEdit(item),
                                  [t('maintenanceSchedule.downloadPDF')]: () =>
                                    generatePDF(item, apiBaseUrl),
                                  [t('maintenanceSchedule.duplicate')]: () => handleDuplicate(item),
                                  [t('maintenanceSchedule.delete')]: () =>
                                    handleDelete(item.scheduleid),
                                }
                              : {
                                  [t('maintenanceSchedule.downloadPDF')]: () =>
                                    generatePDF(item, apiBaseUrl),
                                }
                          }
                        />
                      </div>
                    )})}

                  </div>

                </>

              )}

              {maintenanceList.overdue.length > 0 && (

                <>

                  <div className="content__section-header">
                    <div className="content__section-title">
                      {t('maintenanceSchedule.overdue')}
                      <div className="content__section-title-description">
                        {t('maintenanceSchedule.overdueDesc')}
                      </div>
                    </div>
                  </div>

                  <div className="content__machine-list">

                    {maintenanceList.overdue.filter((item) => machines.find((m) => m.machineid === item.machineid))
                    .map((item) => {
                      const machine = machines.find((m) => m.machineid === item.machineid);
                      return (
                      <div key={item.scheduleid}>
                        <BoxItem
                          color="red"
                          title={`${
                            machines.find((m) => m.machineid === item.machineid)?.machine ||
                            t('maintenanceSchedule.unknownMachine')
                          }`}
                          infoVisible={{
                            [t('maintenanceSchedule.scheduled')]: formatDateByLocale(
                              Math.floor(item.next_maintenance_date),
                              i18n.language,
                              false
                            ),
                            [t('maintenanceSchedule.repeat')]:
                              item.repeat_enabled && item.repeat_interval
                                ? item.maintenance_type === 'custom'
                                  ? t('maintenanceSchedule.repeatsEvery', {
                                      days: item.repeat_interval / 86400,
                                    })
                                  : t(`maintenanceSchedule.${item.maintenance_type}`)
                                : 'N/A',
                                [t('maintenanceSchedule.Group')]: machines.find((m) => m.machineid === item.machineid)?.dncmaxgroupname || t('maintenanceSchedule.unknownGroup'),
                                [t('maintenanceSchedule.userid')]: item.operator || 'None',
                          }}
                          desc={item.descHeader || null}
                          buttonAction={
                            item.status !== 'Open'
                              ? {
                                  name: t('maintenanceSchedule.start'),
                                  handler: () => handleOpen(item),
                                }
                              : {
                                  name: t('maintenanceSchedule.continue'),
                                  handler: () => handleOpen(item),
                                }
                          }
                          options={
                            item.status !== 'Open'
                              ? {
                                  [t('maintenanceSchedule.edit')]: () => handleEdit(item),
                                  [t('maintenanceSchedule.downloadPDF')]: () =>
                                    generatePDF(item, apiBaseUrl),
                                  [t('maintenanceSchedule.duplicate')]: () => handleDuplicate(item),
                                  [t('maintenanceSchedule.delete')]: () =>
                                    handleDelete(item.scheduleid),
                                }
                              : {
                                  [t('maintenanceSchedule.downloadPDF')]: () =>
                                    generatePDF(item, apiBaseUrl),
                                }
                          }
                        />
                      </div>
                    )})}

                  </div>

                </>

              )}

              {maintenanceList.today.length > 0 && (

                <>

                  <div className="content__section-header">
                    <div className="content__section-title">
                      {t('maintenanceSchedule.todaysTasks')}
                      <div className="content__section-title-description">
                        {t('maintenanceSchedule.todayDesc')}
                      </div>
                    </div>
                  </div>

                  <div className="content__machine-list">

                    {maintenanceList.today.filter((item) => machines.find((m) => m.machineid === item.machineid))
  .map((item) => {
    const machine = machines.find((m) => m.machineid === item.machineid);
    return (
                      <div key={item.scheduleid}>
                        <BoxItem
                          color="grey"
                          title={`${
                            machines.find((m) => m.machineid === item.machineid)?.machine ||
                            t('maintenanceSchedule.unknownMachine')
                          }`}
                          infoVisible={{
                            [t('maintenanceSchedule.scheduled')]: formatDateByLocale(
                              Math.floor(item.next_maintenance_date),
                              i18n.language,
                              false
                            ),
                            [t('maintenanceSchedule.repeat')]:
                              item.repeat_enabled && item.repeat_interval
                                ? item.maintenance_type === 'custom'
                                  ? t('maintenanceSchedule.repeatsEvery', {
                                      days: item.repeat_interval / 86400,
                                    })
                                  : t(`maintenanceSchedule.${item.maintenance_type}`)
                                : 'N/A',
                            [t('maintenanceSchedule.Group')]: 
                                machines.find((m) => m.machineid === item.machineid)?.dncmaxgroupname || t('maintenanceSchedule.unknownGroup'),
                                [t('maintenanceSchedule.userid')]: item.operator || 'None',
                          }}
                          desc={item.descHeader || null}
                          buttonAction={
                            item.status !== 'Open'
                              ? {
                                  name: t('maintenanceSchedule.start'),
                                  handler: () => handleOpen(item),
                                }
                              : {
                                  name: t('maintenanceSchedule.continue'),
                                  handler: () => handleOpen(item),
                                }
                          }
                          options={
                            item.status !== 'Open'
                              ? {
                                  [t('maintenanceSchedule.edit')]: () => handleEdit(item),
                                  [t('maintenanceSchedule.downloadPDF')]: () =>
                                    generatePDF(item, apiBaseUrl),
                                  [t('maintenanceSchedule.duplicate')]: () => handleDuplicate(item),
                                  [t('maintenanceSchedule.delete')]: () =>
                                    handleDelete(item.scheduleid),
                                }
                              : {
                                  [t('maintenanceSchedule.downloadPDF')]: () =>
                                    generatePDF(item, apiBaseUrl),
                                }
                          }
                        />
                      </div>
                    )})}

                  </div>

                </>

              )}

              {maintenanceList.tomorrow.length > 0 && (

                <>

                  <div className="content__section-header">
                    <div className="content__section-title">
                      {t('maintenanceSchedule.tomorrowsTasks')}
                      <div className="content__section-title-description">
                        {t('maintenanceSchedule.tomorrowDesc')}
                      </div>
                    </div>
                  </div>

                  <div className="content__machine-list">

                    {maintenanceList.tomorrow.filter((item) => machines.find((m) => m.machineid === item.machineid))
  .map((item) => {
    const machine = machines.find((m) => m.machineid === item.machineid);
    return (
                      <div key={item.scheduleid}>
                        <BoxItem
                          color="grey"
                          title={`${
                            machines.find((m) => m.machineid === item.machineid)?.machine ||
                            t('maintenanceSchedule.unknownMachine')
                          }`}
                          infoVisible={{
                            [t('maintenanceSchedule.scheduled')]: formatDateByLocale(
                              Math.floor(item.next_maintenance_date),
                              i18n.language,
                              false
                            ),
                            [t('maintenanceSchedule.repeat')]:
                              item.repeat_enabled && item.repeat_interval
                                ? item.maintenance_type === 'custom'
                                  ? t('maintenanceSchedule.repeatsEvery', {
                                      days: item.repeat_interval / 86400,
                                    })
                                  : t(`maintenanceSchedule.${item.maintenance_type}`)
                                : 'N/A',
                                [t('maintenanceSchedule.Group')]: machines.find((m) => m.machineid === item.machineid)?.dncmaxgroupname || t('maintenanceSchedule.unknownGroup'),
                                [t('maintenanceSchedule.userid')]: item.operator || 'None',
                          }}
						
                          desc={item.descHeader || null}
                          buttonAction={
                            item.status !== 'Open'
                              ? {
                                  name: t('maintenanceSchedule.start'),
                                  handler: () => handleOpen(item),
                                }
                              : {
                                  name: t('maintenanceSchedule.continue'),
                                  handler: () => handleOpen(item),
                                }
                          }
                          options={
                            item.status !== 'Open'
                              ? {
                                  [t('maintenanceSchedule.edit')]: () => handleEdit(item),
                                  [t('maintenanceSchedule.downloadPDF')]: () =>
                                    generatePDF(item, apiBaseUrl),
                                  [t('maintenanceSchedule.duplicate')]: () => handleDuplicate(item),
                                  [t('maintenanceSchedule.delete')]: () =>
                                    handleDelete(item.scheduleid),
                                }
                              : {
                                  [t('maintenanceSchedule.downloadPDF')]: () =>
                                    generatePDF(item, apiBaseUrl),
                                }
                          }
                        />
                      </div>
                    )})}

                  </div>

                </>

              )}

              {maintenanceList.upcoming.length > 0 && (

                <>

                  <div className="content__section-header">
                    <div className="content__section-title">
                      {t('maintenanceSchedule.upcoming')}
                      <div className="content__section-title-description">
                        {t('maintenanceSchedule.upcomingDesc')}
                      </div>
                    </div>
                  </div>

                  <div className="content__machine-list">

                    {maintenanceList.upcoming.filter((item) => machines.find((m) => m.machineid === item.machineid))
  .map((item) => {
    const machine = machines.find((m) => m.machineid === item.machineid);
    return (
                      <div key={item.scheduleid}>
                        <BoxItem
                          color="grey"
                          title={`${
                            machines.find((m) => m.machineid === item.machineid)?.machine ||
                            t('maintenanceSchedule.unknownMachine')
                          }`}
                          infoVisible={{
                            [t('maintenanceSchedule.scheduled')]: formatDateByLocale(
                              Math.floor(item.next_maintenance_date),
                              i18n.language,
                              false
                            ),
                            [t('maintenanceSchedule.repeat')]:
                              item.repeat_enabled && item.repeat_interval
                                ? item.maintenance_type === 'custom'
                                  ? t('maintenanceSchedule.repeatsEvery', {
                                      days: item.repeat_interval / 86400,
                                    })
                                  : t(`maintenanceSchedule.${item.maintenance_type}`)
                                : 'N/A',
                                [t('maintenanceSchedule.Group')]: machines.find((m) => m.machineid === item.machineid)?.dncmaxgroupname || t('maintenanceSchedule.unknownGroup'),
                                [t('maintenanceSchedule.userid')]: item.operator || 'None',
                          }}
                          desc={item.descHeader || null}
                          buttonAction={
                            item.status !== 'Open'
                              ? {
                                  name: t('maintenanceSchedule.start'),
                                  handler: () => handleOpen(item),
                                }
                              : {
                                  name: t('maintenanceSchedule.continue'),
                                  handler: () => handleOpen(item),
                                }
                          }
                          options={
                            item.status !== 'Open'
                              ? {
                                  [t('maintenanceSchedule.edit')]: () => handleEdit(item),
                                  [t('maintenanceSchedule.downloadPDF')]: () =>
                                    generatePDF(item, apiBaseUrl),
                                  [t('maintenanceSchedule.duplicate')]: () => handleDuplicate(item),
                                  [t('maintenanceSchedule.delete')]: () =>
                                    handleDelete(item.scheduleid),
                                }
                              : {
                                  [t('maintenanceSchedule.downloadPDF')]: () =>
                                    generatePDF(item, apiBaseUrl),
                                }
                          }
                        />
                      </div>
                    )})}

                  </div>

                </>

              )}
              {closedList.length > 0 && (
  <>
    <div className="content__section-header">
      <div className="content__section-title">
        {t('maintenanceSchedule.closed')}
        <div className="content__section-title-description">
          {t('maintenanceSchedule.closedDesc')}
        </div>
      </div>
    </div>

    <div className="content__machine-list">
      {closedList.filter((item) => machines.find((m) => m.machineid === item.machineid))
  .map((item) => {
    const machine = machines.find((m) => m.machineid === item.machineid);
    return (
        <div key={item.scheduleid}>
          <BoxItem
            // You can pick a color or style for closed
            color="grey"
            title={
              machines.find((m) => m.machineid === item.machineid)?.machine ||
              t('maintenanceSchedule.unknownMachine')
            }
            infoVisible={{
              [t('maintenanceSchedule.scheduled')]: formatDateByLocale(
                Math.floor(item.next_maintenance_date),
                i18n.language,
                false
              ),
              [t('maintenanceSchedule.repeat')]:
                item.repeat_enabled && item.repeat_interval
                  ? item.maintenance_type === 'custom'
                    ? t('maintenanceSchedule.repeatsEvery', {
                        days: item.repeat_interval / 86400,
                      })
                    : t(`maintenanceSchedule.${item.maintenance_type}`)
                  : 'N/A',
                  [t('maintenanceSchedule.Group')]: machines.find((m) => m.machineid === item.machineid)?.dncmaxgroupname || t('maintenanceSchedule.unknownGroup'),
                  [t('maintenanceSchedule.userid')]: item.operator || 'None',
            }}
            desc={item.descHeader || null}
            // “Closed” items typically no longer have a "Start" or "Continue" button
            buttonAction={null}
            options={{
              [t('maintenanceSchedule.downloadPDF')]: () => generatePDF(item, apiBaseUrl),
              // or allow "duplicate" etc. if you want
              [t('maintenanceSchedule.duplicate')]: () => handleDuplicate(item),
              [t('maintenanceSchedule.delete')]: () => handleDelete(item.scheduleid),
            }}
          />
        </div>
      )})}




    </div>
  </>
)}
			*/}

      
            </div>
		  
          </div>

        )}

      </DndProvider>

    </>

  );
};

export default MaintenanceSchedule;
